import { EventService } from "@/services/events";
import { Deporte } from "@/types/category";
import { PersistentCache } from "@/utils/cache";
import { useEffect, useState } from "react";

const eventService = EventService.getInstance();

/**
 * Persistent cache for the sports that have at least one event.
 *
 * The available sports are language-independent (the backend returns them by
 * `idDeporte` + lowercase name and the frontend translates by id), so a single
 * cache entry is enough. Stored in localStorage under the key `available_sports`
 * (PersistentCache composes the key as `${prefix}_${key}`).
 */
// v2: el backend ahora incluye deportes con grafías alias (p.ej. obstacles vía
// "obstaculos"). Se sube la versión de la clave para invalidar la lista cacheada
// en localStorage de los clientes sin tener que borrarla a mano.
const CACHE_KEY = "sports_v2"; // localStorage key => "available_sports_v2"
const availableSportsCache = new PersistentCache<Deporte[]>("available", {
	ttl: 30 * 60 * 1000, // 30 minutes
	disableInDev: false, // enabled in development for testing
});

/**
 * Module-level in-flight promise so that the multiple components that use this
 * hook at the same time (SearchBar, CategoryNav, CategoryMenu) share a single
 * request instead of each firing its own. Once resolved, the result is served
 * from the persistent cache.
 */
let inFlight: Promise<Deporte[] | null> | null = null;

const loadAvailableSports = (): Promise<Deporte[] | null> => {
	if (inFlight) {
		return inFlight;
	}

	inFlight = availableSportsCache
		.getOrSet(CACHE_KEY, async () => {
			const response = await eventService.getAvailableSports();

			if (response.statusCode === 200 && response.data) {
				return response.data;
			}

			// Throw so the (bad) result is not cached and callers can fall back.
			throw new Error(response.error || "Error getting available sports");
		})
		.catch((error) => {
			console.error("Error getting cached sports", error);
			return null;
		})
		.finally(() => {
			inFlight = null;
		});

	return inFlight;
};

export const useCachedSports = () => {
	const [cachedSports, setCachedSports] = useState<Deporte[] | null>(null);

	useEffect(() => {
		let mounted = true;

		loadAvailableSports().then((data) => {
			if (mounted && data) {
				setCachedSports(data);
			}
		});

		return () => {
			mounted = false;
		};
	}, []);

	return cachedSports;
};
